'use client';

import Link from 'next/link';
import { BedSingle, Bath, User } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import PropertyImageSlider from '../PropertyImageSlider';
import { usePropertyCard } from './usePropertyCard';
import PropertyTitle from './PropertyTitle';
import LocationBadge from './LocationBadge';
import PropertyPrice from './PropertyPrice';
import PremiumBadge from './PremiumBadge';
import ViewCount from './ViewCount';
import PropertyPerks from './PropertyPerks';
import BasicBadge from './BasicBadge';
import { cn } from '@/lib/utils';
import { PropertyListing } from '@/app/actions/serverActions';
import posthog from 'posthog-js';

interface PropertyCardProps {
  property: PropertyListing;
  featured: boolean;
}

export function PropertyCard({ property, featured = false }: PropertyCardProps) {
  const { isMobile, shouldShowHoverEffects, handleMouseEnter, handleMouseLeave, getPropertyLink } =
    usePropertyCard();

  const propertyLink = getPropertyLink(property.id);

  const capturePostHogEventProductClicked = () => {
    posthog.capture('Product Clicked', {
      product_id: property.id,
      sku: property.id,
      category: 'properties',
      name: property.title,
      price: property.price,
      quantity: 1,
      position: 1,
      url: propertyLink,
      image_url: property.images[0],
    });
  };

  return (
    <Card
      className={cn(
        'group relative overflow-hidden transition-all duration-300 ease-in-out w-full shadow-2xl rounded-3xl p-1 bg-gradient-to-b from-secondary via-accent to-brand-blue',
        shouldShowHoverEffects ? 'scale-[1.01] z-10' : 'scale-100 z-0'
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <CardContent className="p-0 bg-white rounded-3xl">
        <div className="relative">
          <PropertyImageSlider
            propertyLink={propertyLink}
            images={property.images}
            height={featured ? 80 : 64}
            featured={featured}
          />

          {(featured || property.isFeatured) && (
            <PremiumBadge shouldShowHoverEffects={shouldShowHoverEffects} />
          )}

          <ViewCount
            viewCount={property.views}
            shouldShowHoverEffects={shouldShowHoverEffects || isMobile}
          />
        </div>

        <Link
          href={propertyLink}
          className="block transition-all"
          onClick={capturePostHogEventProductClicked}
        >
          <div className={cn('p-4 min-h-36 flex flex-col gap-2')}>
            <LocationBadge
              location={property.destinationLabel}
              area={property.areaLabel}
              shouldShowHoverEffects={shouldShowHoverEffects}
            />

            <PropertyTitle
              title={property.title}
              featured={featured}
              shouldShowHoverEffects={shouldShowHoverEffects}
            />

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <BasicBadge icon={<User className="w-5 h-5" />} value={property.maxGuests} />
              <div className="w-1 h-1 bg-primary rounded-full" />
              <BasicBadge icon={<BedSingle className="w-5 h-5" />} value={property.beds} />
              <div className="w-1 h-1 bg-primary rounded-full" />
              <BasicBadge icon={<Bath className="w-5 h-5" />} value={property.baths} />
            </div>

            <div className="flex">
              <PropertyPrice
                price={property.price}
                shouldShowHoverEffects={shouldShowHoverEffects}
              />
            </div>

            <PropertyPerks
              perks={property.perks || []}
              isMobile={isMobile}
              shouldShowHoverEffects={shouldShowHoverEffects}
              featured={featured}
            />
          </div>
        </Link>
      </CardContent>
    </Card>
  );
}

export default PropertyCard;
